import { useState, useEffect, useCallback } from 'react';
import {
  fetchApplications,
  updateApplicationStatus,
  deleteApplication,
  setAdminToken,
  clearAdminToken,
  isAdminAuthenticated,
  verifyAdminToken,
  downloadExcelExport,
  type ApplicationRecord,
} from '@/api/registrationApi';
import {
  Download,
  Trash2,
  LogOut,
  Search,
  Loader2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS: Record<string, string> = {
  new: 'Новая',
  reviewing: 'На рассмотрении',
  accepted: 'Принята',
  rejected: 'Отклонена',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  reviewing: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const COMPETENCE_LABELS: Record<string, string> = {
  'game-dev': 'Разработка компьютерных игр',
  '3d-modeling': '3D-моделирование для компьютерных игр',
  'game-design': 'Геймдизайн',
};

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const valid = await verifyAdminToken(token.trim());
      if (!valid) {
        setError('Неверный токен доступа');
        return;
      }
      setAdminToken(token.trim());
      onLogin();
      toast.success('Авторизация успешна');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Сервер недоступен. Убедитесь, что запущен npm run dev:server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-20">
      <div className="card">
        <h2 className="text-2xl font-bold mb-6 text-center">Вход в админ-панель</h2>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Секретный токен</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Введите токен доступа"
              required
              className="input-field"
            />
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Войти
          </button>
        </form>
        <p className="text-xs text-gray-500 mt-4 text-center">Токен указан в .env.server (ADMIN_TOKEN)</p>
      </div>
    </div>
  );
}

function ApplicationsTable({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [applications, setApplications] = useState<ApplicationRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<ApplicationRecord | null>(null);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApplications({
        page,
        limit,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setApplications(data.items);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof Error && err.message === 'Требуется авторизация') {
        toast.error('Сессия истекла. Войдите снова.');
        onUnauthorized();
      } else {
        toast.error(err instanceof Error ? err.message : 'Ошибка загрузки');
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, onUnauthorized]);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  const totalPages = Math.ceil(total / limit);

  const handleStatusChange = async (id: string, status: ApplicationRecord['status']) => {
    setActionLoading(id);
    try {
      await updateApplicationStatus(id, status);
      toast.success(`Статус: "${STATUS_LABELS[status]}"`);
      loadApplications();
      if (selectedApp?.id === id) setSelectedApp({ ...selectedApp, status });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить эту заявку?')) return;
    setActionLoading(id);
    try {
      await deleteApplication(id);
      toast.success('Заявка удалена');
      loadApplications();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка удаления');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    try {
      await downloadExcelExport();
      toast.success('Excel файл скачан');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Ошибка экспорта');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              placeholder="Поиск по ФИО, email, городу..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input-field pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="input-field"
          >
            <option value="">Все статусы</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={loadApplications} disabled={loading} className="btn-secondary flex items-center disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button type="button" onClick={handleExport} className="btn-secondary flex items-center">
            <Download className="h-4 w-4 mr-2" />Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-4 text-center"><div className="text-2xl font-bold">{total}</div><div className="text-sm text-gray-500">Всего</div></div>
        <div className="card p-4 text-center"><div className="text-2xl font-bold text-blue-600">{applications.filter(a => a.status === 'new').length}</div><div className="text-sm text-gray-500">Новых</div></div>
        <div className="card p-4 text-center"><div className="text-2xl font-bold text-green-600">{applications.filter(a => a.status === 'accepted').length}</div><div className="text-sm text-gray-500">Принято</div></div>
        <div className="card p-4 text-center"><div className="text-2xl font-bold text-red-600">{applications.filter(a => a.status === 'rejected').length}</div><div className="text-sm text-gray-500">Отклонено</div></div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-500" /></div>
      ) : (
        <>
          <div className="border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ФИО</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Направление</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Контакты</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Город</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-500">Заявок не найдено</td></tr>
                  ) : (
                    applications.map((app) => (
                      <tr
                        key={app.id}
                        className={`cursor-pointer hover:bg-gray-50 ${selectedApp?.id === app.id ? 'bg-gray-50' : ''}`}
                        onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap font-medium">{app.fullName}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{COMPETENCE_LABELS[app.competence] || app.competence}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{app.email}<div className="text-gray-500 text-xs">{app.phone}</div></div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{app.city}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[app.status]}`}>
                            {STATUS_LABELS[app.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{new Date(app.createdAt).toLocaleDateString('ru-RU')}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            type="button"
                            disabled={actionLoading === app.id}
                            onClick={(e) => { e.stopPropagation(); handleDelete(app.id); }}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50 inline-flex items-center"
                          >
                            {actionLoading === app.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <button className="btn-secondary disabled:opacity-50" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Назад</button>
              <span className="flex items-center px-4 text-sm text-gray-600">Страница {page} из {totalPages} ({total} заявок)</span>
              <button className="btn-secondary disabled:opacity-50" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Вперёд</button>
            </div>
          )}
        </>
      )}

      {selectedApp && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedApp(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto card" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-xl font-bold">Детали заявки</h3>
                <button type="button" onClick={() => setSelectedApp(null)} className="text-gray-400 hover:text-gray-600"><XCircle className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4">
                <div><label className="text-sm font-medium text-gray-500">ФИО</label><p className="font-medium">{selectedApp.fullName}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Email</label><p>{selectedApp.email}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Телефон</label><p>{selectedApp.phone}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Дата рождения</label><p>{selectedApp.birthDate}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Город</label><p>{selectedApp.city}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Учреждение</label><p>{selectedApp.institution}</p></div>
                <div><label className="text-sm font-medium text-gray-500">Направление</label><p>{COMPETENCE_LABELS[selectedApp.competence] || selectedApp.competence}</p></div>
                {selectedApp.portfolioLinks && selectedApp.portfolioLinks.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Портфолио (ссылки)</label>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {selectedApp.portfolioLinks.map((link, i) => (
                        <li key={i}>
                          <a href={link} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-all text-sm">{link}</a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Статус</label>
                  <p className="mt-1"><span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedApp.status]}`}>{STATUS_LABELS[selectedApp.status]}</span></p>
                </div>
                <div><label className="text-sm font-medium text-gray-500">Дата подачи</label><p>{new Date(selectedApp.createdAt).toLocaleString('ru-RU')}</p></div>
                <div className="pt-4 border-t">
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Изменить статус</label>
                  <div className="flex flex-wrap gap-2">
                    {(['new', 'reviewing', 'accepted', 'rejected'] as const).map((status) => (
                      <button
                        key={status}
                        type="button"
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium ${selectedApp.status === status ? 'btn-primary' : 'btn-secondary'}`}
                        disabled={actionLoading === selectedApp.id}
                        onClick={() => handleStatusChange(selectedApp.id, status)}
                      >
                        {STATUS_LABELS[status]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminApplicationsPage() {
  const [authenticated, setAuthenticated] = useState(isAdminAuthenticated());

  const handleLogin = useCallback(() => setAuthenticated(true), []);

  const handleLogout = useCallback(() => {
    clearAdminToken();
    setAuthenticated(false);
    toast.info('Вы вышли из админ-панели');
  }, []);

  if (!authenticated) return <AdminLogin onLogin={handleLogin} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Заявки на участие</h1>
          <p className="text-gray-600 mt-1">Управление заявками на участие в ОЧКИ 2026</p>
        </div>
        <button type="button" onClick={handleLogout} className="btn-secondary flex items-center">
          <LogOut className="h-4 w-4 mr-2" />Выйти
        </button>
      </div>
      <ApplicationsTable onUnauthorized={handleLogout} />
    </div>
  );
}
